import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarClock,
  CircleDollarSign,
  ListTodo,
  type LucideIcon,
  TrendingDown,
} from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { Link } from "react-router";

import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";

import type { CrmDataProvider, Dashboard } from "../providers/types";
import { usePermission } from "../providers/usePermission";
import { formatCents } from "./format";
import { EmptyState, GraphError, GraphLoading } from "./GraphStatus";
import { RecordCard } from "./record/RecordCard";
import { humanize, MUTED, RECORD_LINK } from "./record/styles";

interface SavedViewResult {
  id: string;
  label: string;
  href: string;
  primary_type?: string | null;
  possible_duplicate?: boolean;
}

interface SavedView {
  id: string;
  name: string;
  object_type: string;
  query?: { kind?: string } | null;
  results: SavedViewResult[];
}

const COMPACT_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

function greeting(name: string | undefined): string {
  const hour = new Date().getHours();
  const part = hour < 12 ? "morning" : hour < 18 ? "afternoon" : "evening";
  const first = name?.split(/\s+/)[0];
  return first ? `Good ${part}, ${first}` : `Good ${part}`;
}

/**
 * Home (W12 U5): a greeting, four tiles, the busiest loan pipeline by stage,
 * open tasks to tick off, and the tenant's saved views. The saved views keep
 * every row (scrolling inside their cards): the walkthrough starts from them.
 */
export function GraphHome() {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: identity } = useGetIdentity();
  const dashboard = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => dataProvider.getDashboard(),
  });
  const views = useGetList<SavedView>("saved_views", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "name", order: "ASC" },
  });

  if (views.isPending) return <GraphLoading shape="home" />;
  if (views.error) return <GraphError error={views.error} noun="home page" />;

  // Open tasks get their own, tickable card below.
  const listed = (views.data ?? []).filter(
    (view) => view.query?.kind !== "open_tasks",
  );
  const d = dashboard.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {greeting(identity?.fullName)}
        </h1>
        <p className="text-sm text-muted-foreground">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long",
            month: "long",
            day: "numeric",
          })}
        </p>
      </div>

      <div className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-3 sm:gap-4 xl:grid-cols-4">
        <StatTile
          Icon={CircleDollarSign}
          label="Open loans"
          value={d?.open_loans}
          detail={
            d
              ? `${COMPACT_USD.format(d.open_amount_cents / 100)} in pipeline`
              : null
          }
        />
        <StatTile
          Icon={ListTodo}
          label="Open tasks"
          value={d?.open_tasks}
          detail={
            d
              ? d.overdue_tasks
                ? `${d.overdue_tasks} overdue`
                : "None overdue"
              : null
          }
          isAlert={Boolean(d?.overdue_tasks)}
        />
        <StatTile
          Icon={TrendingDown}
          label="In the money"
          value={d?.in_the_money}
          detail="Borrowers who could refinance"
        />
        <StatTile
          Icon={CalendarClock}
          label="Closing in 30 days"
          value={d?.closing_soon}
          detail="By estimated close date"
        />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <PipelineChart pipeline={d?.pipeline ?? null} />
        </div>
        <div className="space-y-6">
          <OffTrack loans={d?.off_track ?? []} />
          <OpenTasks tasks={d?.tasks ?? []} />
        </div>
      </div>

      {listed.length === 0 ? (
        <Card className="py-0">
          <EmptyState title="No saved views yet." />
        </Card>
      ) : (
        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 md:grid-cols-2 xl:grid-cols-3">
          {listed.map((view) => (
            <RecordCard
              key={view.id}
              title={view.name}
              count={view.results.length}
              action={
                <Link
                  to={`/views/${view.id}`}
                  className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  View all
                </Link>
              }
              isEmpty={view.results.length === 0}
            >
              <ul className="-mx-2 max-h-72 divide-y overflow-y-auto">
                {view.results.map((row) => (
                  <li key={row.id} className="px-2 py-2 text-sm">
                    <Link className={RECORD_LINK} to={row.href}>
                      {row.label}
                    </Link>
                    {row.primary_type ? (
                      <span className={MUTED}>
                        {" "}
                        · {humanize(row.primary_type)}
                      </span>
                    ) : null}
                    {row.possible_duplicate ? (
                      <span className="text-amber-700 dark:text-amber-400">
                        {" "}
                        · possible duplicate
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </RecordCard>
          ))}
        </div>
      )}
    </div>
  );
}

function StatTile({
  Icon,
  label,
  value,
  detail,
  isAlert = false,
}: {
  Icon: LucideIcon;
  label: string;
  value: number | undefined;
  detail: string | null;
  isAlert?: boolean;
}) {
  return (
    <div className="rounded-xl border bg-card px-4 py-3 shadow-xs sm:px-5 sm:py-4">
      <div className="flex items-center justify-between text-xs text-muted-foreground sm:text-sm">
        <span>{label}</span>
        <Icon className="size-4" />
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums tracking-tight sm:mt-2 sm:text-3xl">
        {value ?? "—"}
      </div>
      <div
        className={cn(
          "mt-1 text-xs",
          isAlert ? "text-destructive" : "text-muted-foreground",
        )}
      >
        {detail ?? " "}
      </div>
    </div>
  );
}

/** Open loans per stage of the busiest loan pipeline, as CSS bars. */
function PipelineChart({ pipeline }: { pipeline: Dashboard["pipeline"] }) {
  const max = Math.max(1, ...(pipeline?.stages ?? []).map((s) => s.deal_count));
  return (
    <RecordCard
      title={pipeline ? `Pipeline · ${pipeline.name}` : "Pipeline"}
      isEmpty={!pipeline}
      emptyText="No open loans."
      action={
        pipeline ? (
          <Link
            to={`/deals?pipeline=${pipeline.id}`}
            className="text-xs text-muted-foreground hover:text-foreground hover:underline"
          >
            Open board
          </Link>
        ) : null
      }
    >
      <ul className="space-y-3">
        {(pipeline?.stages ?? []).map((stage) => (
          <li
            key={stage.id}
            className="grid grid-cols-[minmax(0,7rem)_1fr_auto] items-center gap-3 text-sm sm:grid-cols-[minmax(0,11rem)_1fr_auto]"
          >
            <span className="truncate text-muted-foreground">
              {stage.label}
            </span>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary"
                style={{ width: `${(stage.deal_count / max) * 100}%` }}
              />
            </div>
            <span className="text-right tabular-nums sm:w-28">
              {stage.deal_count}
              <span className={MUTED + " hidden sm:inline"}>
                {" "}
                · {stage.amount_cents ? formatCents(stage.amount_cents) : "$0"}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </RecordCard>
  );
}

/** Loans that will miss their close date unless something moves (W15 M8). */
function OffTrack({ loans }: { loans: NonNullable<Dashboard["off_track"]> }) {
  if (!loans.length) return null;
  return (
    <RecordCard title="Off track" count={loans.length}>
      <ul className="space-y-3">
        {loans.map((loan) => (
          <li key={`${loan.deal_id}-${loan.lien_position}`} className="text-sm">
            <Link className={RECORD_LINK + " block truncate"} to={loan.href}>
              {loan.deal_name}
            </Link>
            <div className="truncate text-xs text-destructive">
              {loan.reason}
            </div>
          </li>
        ))}
      </ul>
    </RecordCard>
  );
}

/** The soonest open tasks, tickable by roles that may complete them. */
function OpenTasks({ tasks }: { tasks: Dashboard["tasks"] }) {
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const canComplete = usePermission("notes_tasks");
  const complete = useMutation({
    mutationFn: (taskId: string) => dataProvider.setTaskCompleted(taskId, true),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: ["saved_views"] });
    },
    onError: () => notify("The task could not be completed", { type: "error" }),
  });
  return (
    <RecordCard
      title="Open tasks"
      count={tasks.length}
      isEmpty={tasks.length === 0}
      emptyText="Nothing to do."
    >
      <ul className="space-y-3">
        {tasks.map((task) => (
          <li key={task.id} className="flex items-start gap-3 text-sm">
            <Checkbox
              className="mt-0.5"
              disabled={!canComplete || complete.isPending}
              aria-label={`Complete ${task.title}`}
              onCheckedChange={(checked) =>
                checked === true ? complete.mutate(task.id) : undefined
              }
            />
            <div className="min-w-0">
              <Link className={RECORD_LINK + " block truncate"} to={task.href}>
                {task.title}
              </Link>
              <div className="truncate text-xs text-muted-foreground">
                {task.priority === "urgent" || task.priority === "high" ? (
                  <span className="font-medium text-foreground">
                    {task.priority} ·{" "}
                  </span>
                ) : null}
                {task.subject_label}
                {task.due_on ? (
                  <span className={task.is_overdue ? "text-destructive" : ""}>
                    {" "}
                    · due {task.due_on}
                  </span>
                ) : null}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </RecordCard>
  );
}
